import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import LoadingPlaceholder from '../components/LoadingPlaceholder'
import Loader from '../components/Loading'
import { getClient, createClient, deleteClient } from '../api/crm'
import { getSessionAuth } from '../utils/sessionAuth'
import { ensureSessionUserId, getSessionUserId } from '../utils/sessionUser'
import '../style/views/ContactDetailView.css'

type Client = {
  id: string
  first_name?: string
  last_name?: string
  email?: string
  cell?: string
  company?: string
  notes?: string
  summary?: string
}

type SelectedValues = {
  first_name: string
  last_name: string
  email: string
  cell: string
  company: string
}

type FieldGroupProps = {
  clients: Client[]
  field: keyof SelectedValues
  label: string
  selectedValues: SelectedValues
  handleValueSelect: (field: keyof SelectedValues, value: string) => void
}

function FieldGroup({ clients, field, label, selectedValues, handleValueSelect }: FieldGroupProps) {
  const uniqueValues = Array.from(new Set(clients.flatMap(c => c[field] ? [c[field]!] : [])))

  return (
    <div className="merge-field-container">
      <div className="merge-field-header">
        <h3 style={{ margin: 0, fontSize: 14, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</h3>
      </div>

      <div className="merge-options-scroller">
        {uniqueValues.map(val => (
          <button
            type="button"
            key={val}
            className={`tile-pill ${selectedValues[field] === val ? 'active' : ''}`}
            onClick={() => handleValueSelect(field, val)}
          >
            {val}
          </button>
        ))}
      </div>

      <input
        className="edit-input"
        style={{ marginTop: 8, width: '100%' }}
        placeholder={`Enter custom ${label.toLowerCase()}...`}
        value={selectedValues[field]}
        onChange={(e) => handleValueSelect(field, e.target.value)}
      />
    </div>
  )
}

function ContactMergeView() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const ids = useMemo(() => searchParams.get('ids')?.split(',') || [], [searchParams])
  
  const [userId, setUserId] = useState<string | null>(() => getSessionUserId())
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [merging, setMerging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [selectedValues, setSelectedValues] = useState<SelectedValues>({
    first_name: '',
    last_name: '',
    email: '',
    cell: '',
    company: '',
  })

  useEffect(() => {
    if (ids.length < 2) {
      navigate('/contacts')
      return
    }

    const controller = new AbortController()
    const loadClients = async () => {
      try {
        const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
        if (!ensuredId) return
        if (!userId) setUserId(ensuredId)

        const results = await Promise.all(
          ids.map(id =>
            getClient(id, ensuredId, controller.signal).catch(() => null)
          )
        )
        const fetchedClients: Client[] = results.flatMap(data =>
          data ? [{ ...data, summary: data.summary || data.notes || '' }] : []
        )
        setClients(fetchedClients)
        
        if (fetchedClients.length > 0) {
          const first = fetchedClients[0]
          setSelectedValues({
            first_name: first.first_name || '',
            last_name: first.last_name || '',
            email: first.email || '',
            cell: first.cell || '',
            company: first.company || '',
          })
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Failed to load contacts for merge', err)
          setError('Failed to load contact details.')
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    loadClients()
    return () => controller.abort()
  }, [ids, userId, navigate])

  const combinedNotes = useMemo(() => {
    return clients
      .flatMap(c => (c.summary || c.notes) ? [c.summary || c.notes!] : [])
      .join('\n\n---\n\n')
  }, [clients])

  const handleValueSelect = (field: keyof typeof selectedValues, value: string) => {
    setSelectedValues(prev => ({ ...prev, [field]: value }))
  }

  const handleMerge = async () => {
    try {
      setMerging(true)
      setError(null)
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) throw new Error('Missing user id')

      await Promise.all(ids.map(id => deleteClient(id, ensuredId)))

      await createClient({
        user_id: ensuredId,
        conversation_id: null,
        client: {
          ...selectedValues,
          notes: combinedNotes,
        },
      })

      navigate('/contacts')
    } catch (err) {
      console.error('Merge failed', err)
      setError(err instanceof Error ? err.message : 'Merge failed. Please try again.')
    } finally {
      setMerging(false)
    }
  }

  if (loading) return <LoadingPlaceholder variant="all" />

  return (
    <>
      {merging && (
        <div className="merge-loader-overlay">
          <div className="merge-loader-content">
            <Loader />
          </div>
        </div>
      )}
      <div className="content-header header-title-bar">
        <h1>Merge Contacts</h1>
        <div className="conversation-actions" style={{ display: 'flex', gap: '8px' }}>
          <button type="button" className="ghost-button" onClick={() => navigate('/contacts')}>
            Cancel
          </button>
          <button
            type="button"
            className="signal-button header-action-button"
            onClick={handleMerge} 
            disabled={merging}
          >
            {merging ? 'Merging...' : 'Confirm Merge'}
          </button>
        </div>
      </div>

      {error && (
        <div className="muted" style={{ marginTop: '6px', color: '#b42318', padding: '0 28px' }}>
          {error}
        </div>
      )}

      <div className="content-panel">
        <div className="card-panel">
          <p className="eyebrow" style={{ marginBottom: 32 }}>Consolidate details from {clients.length} contacts.</p>
          
          <div className="merge-layout-grid" style={{ display: 'grid', gap: 32 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <FieldGroup clients={clients} field="first_name" label="First Name" selectedValues={selectedValues} handleValueSelect={handleValueSelect} />
              <FieldGroup clients={clients} field="last_name" label="Last Name" selectedValues={selectedValues} handleValueSelect={handleValueSelect} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <FieldGroup clients={clients} field="email" label="Email" selectedValues={selectedValues} handleValueSelect={handleValueSelect} />
              <FieldGroup clients={clients} field="cell" label="Phone Number" selectedValues={selectedValues} handleValueSelect={handleValueSelect} />
            </div>

            <FieldGroup clients={clients} field="company" label="Company" selectedValues={selectedValues} handleValueSelect={handleValueSelect} />

            <div className="merge-field-group">
              <h3 style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Combined Notes</h3>
              <div className="notes-block notes-markdown" style={{ opacity: 0.8, maxHeight: 300, overflowY: 'auto' }}>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6 }}>
                  {combinedNotes || 'No notes to combine.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .merge-options-scroller {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding: 4px 0 12px;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .merge-options-scroller::-webkit-scrollbar {
          display: none;
        }
        .merge-field-container .tile-pill {
          flex-shrink: 0;
          white-space: nowrap;
          padding: 8px 14px;
          border-radius: 10px;
          border: 1px solid #e3e7ee;
          background: #fff;
          cursor: pointer;
          font-weight: 500;
          transition: all 140ms ease;
        }
        .merge-field-container .tile-pill.active {
          border-color: var(--core-blue);
          background: rgba(100, 129, 210, 0.1);
          color: var(--core-blue);
          font-weight: 700;
        }
        .merge-field-container .edit-input {
          padding: 12px 14px;
          border-radius: 12px;
          border: 1px solid #e3e7ee;
          background: #fdfdff;
          font-size: 15px;
          transition: all 180ms ease;
        }
        .merge-field-container .edit-input:focus {
          border-color: var(--core-blue);
          background: #fff;
          box-shadow: 0 0 0 4px rgba(100, 129, 210, 0.12);
          outline: none;
        }
        [data-theme='dark'] .merge-field-container .tile-pill {
          background: #1d2638;
          border-color: #344362;
          color: var(--ink-strong);
        }
        [data-theme='dark'] .merge-field-container .edit-input {
          background: #161d2d;
          border-color: #2d3a56;
          color: var(--ink-strong);
        }
        [data-theme='dark'] .merge-field-container .edit-input:focus {
          border-color: var(--core-blue);
          background: #1a2438;
        }
        .merge-loader-overlay {
          position: fixed;
          inset: 0;
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 200ms ease;
        }
        .merge-loader-content {
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }
        .merge-loader-content p {
          font-weight: 700;
          color: var(--core-blue);
          font-size: 18px;
          margin: 0;
        }
        [data-theme='dark'] .merge-loader-overlay {
          background: rgba(15, 20, 31, 0.8);
        }
      `}</style>
    </>
  )
}

export default ContactMergeView
